import React from 'react';
import { useFadeIn } from '../hooks/useFadeIn';
import './Services.css';

const services = [
  {
    num: '01',
    icon: '🔍',
    tag: 'Intelligence',
    title: 'Open-Source Analysis & Audits',
    desc: 'Comprehensive intelligence gathering and audits through open sources and appropriate means, correlated and evaluated in compliance with all applicable local and international regulations.',
  },
  {
    num: '02',
    icon: '🔒',
    tag: 'Confidentiality',
    title: 'Data Privacy & Secure Communications',
    desc: 'End-to-end encrypted communication channels and robust data privacy protocols to ensure your sensitive information remains protected from interception, leaks, and unauthorized access.',
  },
  {
    num: '03',
    icon: '💻',
    tag: 'Technology',
    title: 'Intelligence Software & Analytics',
    desc: 'Purpose-built software solutions that enable organizations to manage, correlate, and act on intelligence data across complex, high-stakes operational environments.',
  },
  {
    num: '04',
    icon: '⚖️',
    tag: 'Compliance',
    title: 'Regulatory Intelligence & Risk Management',
    desc: 'Expert guidance on regulatory intelligence, risk management, and compliance — helping organizations maintain full regulatory standards and mitigate operational risks in sensitive environments.',
  },
  {
    num: '05',
    icon: '🤝',
    tag: 'Community',
    title: 'Intelligence Community R&D',
    desc: 'Strategic R&D partnerships with the broader Intelligence Community — managing services of common concern such as open-source collection and analysis to advance collective capability.',
  },
  {
    num: '06',
    icon: '🎓',
    tag: 'Development',
    title: 'Professional Intelligence Development',
    desc: 'Overall direction and coordination for professional development in intelligence — open to all, fostering the next generation of security and intelligence professionals worldwide.',
  },
];

export default function Services() {
  const headerRef = React.useRef(null);
  const cardRefs = React.useRef([]);

  React.useEffect(() => {
    // Header fade-in
    if (headerRef.current) {
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            headerRef.current.classList.add('visible');
            observer.unobserve(headerRef.current);
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(headerRef.current);
      return () => observer.disconnect();
    }
  }, []);

  React.useEffect(() => {
    // Service cards fade-in
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    cardRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="services-section">
      <div className="services-header fade-in" ref={headerRef}>
        <div>
          <div className="section-label">What We Do</div>
          <h2 className="section-title">
            Intelligence solutions for a <em>complex world.</em>
          </h2>
        </div>
        <div>
          <p className="services-intro">
            Grantsman brings together specialized disciplines under one roof. From counterterrorism
            analysis to analytic software, we deliver end-to-end solutions that address the full
            spectrum of corporate and institutional security challenges — always within regulatory
            bounds.
          </p>
        </div>
      </div>

      <div className="services-grid">
        {services.map((s, i) => (
          <div
            className={`service-card fade-in delay-${(i % 4) + 1}`}
            key={s.num}
            ref={el => cardRefs.current[i] = el}
          >
            <div className="service-num">{s.num}</div>
            <span className="service-icon">{s.icon}</span>
            <div className="service-tag">{s.tag}</div>
            <h3 className="service-title">{s.title}</h3>
            <p className="service-desc">{s.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
